import { Form, Formik } from 'formik';
import { Address } from 'modules/core/domain/interfaces/address';
import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import { TabMenu } from 'ud-ui/components/tab-menu';
import { mapChampionshipSettings } from 'modules/championship/domain/data-mappers/championship-settings.data-mapper';
import { upsertChampionshipSettings } from 'modules/championship/store/championship/actions';
import { championshipDataSelector, championshipISavingSelector } from 'modules/championship/store/championship/selectors';
import ApplicationCompany from './application-company';
import Regulations from './regulations';
import { SportType } from './types';

type ChampionshipRulesFormProps = {
  tournamentId: number;
  championshipId: number;
};

export interface ChampionshipFormValues {
  id: number | null;
  name: string;
  tournamentId: number;
  startDate: Date | null;
  endDate: Date | null;
  settings: ChampionshipSettings;
  address: Address;
}

const ChampionshipRulesForm = (props: ChampionshipRulesFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const championship = useSelector(championshipDataSelector(props.championshipId));
  const isChampionshipSaving = useSelector(championshipISavingSelector);
  const [activeTab, setActiveTab] = useState(0);

  const hasChampionship = !!championship;
  const initialValues: ChampionshipFormValues = hasChampionship
    ? {
        id: championship.id,
        name: championship.name,
        tournamentId: props.tournamentId,
        startDate: championship.startDate ? new Date(championship.startDate) : null,
        endDate: championship.endDate ? new Date(championship.endDate) : null,
        settings: championship.settings,
        address: championship.address
          ? { ...championship.address }
          : {
              city: '',
              addressLine1: '',
              country: '',
            },
      }
    : {
        id: null,
        name: '',
        tournamentId: props.tournamentId,
        startDate: null,
        endDate: null,
        settings: mapChampionshipSettings(),
        address: {
          city: '',
          addressLine1: '',
          country: '',
        },
      };

  const handleSubmit = useCallback(
    (data: any) => {
      const { address } = data;
      let params = data;

      if (address.city !== championship?.address?.city || address.addressLine1 !== championship?.address?.addressLine1) {
        params = {
          ...data,
          address: {
            city: address.city,
            addressLine1: address.addressLine1,
          },
        };
      }

      dispatch(upsertChampionshipSettings(params));
    },
    [championship?.address?.addressLine1, championship?.address?.city, dispatch],
  );

  return (
    <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleSubmit}>
      {(formProps) => (
        <Form onSubmit={formProps.handleSubmit}>
          <div className="mb-5">
            <TabMenu
              tabs={[
                { name: 'Заявочная компания', active: true },
                { name: 'Регламент', active: true },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>

          <div className="w-full gap-6 px-6">
            {formProps.initialValues.id && <input type="hidden" name="id" value={formProps.initialValues.id} />}

            <input type="hidden" name="tournamentId" value={formProps.initialValues.tournamentId} />

            {activeTab === 0 && <ApplicationCompany sportType={championship?.sportType as SportType} />}
            {activeTab === 1 && <Regulations sportType={championship?.sportType as SportType} />}

            <div className="w-1/2"></div>

            <UDButton
              variant="primary"
              type="submit"
              disabled={isChampionshipSaving || !formProps.dirty}
              className="mt-5 mb-3 w-1/2"
              loading={isChampionshipSaving}
            >
              Сохранить
            </UDButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ChampionshipRulesForm;
